import React from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation, gql } from '@apollo/client';
import ApiError from '@/lib/shared-coms/ApiError';
import { Spinner } from 'equasion-lib';
import SalesOrderSummary from './SalesOrderSummary';
import { useRootStore } from '@/lib/providers/RootProvider';
import { useRxPrintEnabled, useSendDocumentsToPrinter } from '@/lib/rxPrint';
import { pickVerifyStepFor } from '@/lib/pickVerifyShip';

const BUNDLE_CANDIDATE_FIELDS = gql`
  fragment BundleCandidateFields on ShippingBundleCandidate {
    id
    vendorSalesOrderNumber
    vendorStatus
    itemCount
    bundleable
    reason
    inBundle
  }
`;

export const CLIENT_ORDER = gql`
  ${BUNDLE_CANDIDATE_FIELDS}
  query salesOrder($id: ID!, $vendorId: ID!) {
    salesOrder(id: $id, vendorId: $vendorId) {
      id
      vendorSalesOrderNumber
      vendorStatus
      clientStatus
      # Puerto Rico shipments get a Certificate of Origin (@/lib/certificateOfOrigin).
      shipsToPuertoRico
      discountAmount
      shippingAmount
      taxAmount
      convenienceFeeAmount
      subtotalAmount
      totalAmount
      createdAt
      updatedAt
      orderItems {
        id
        quantity
        product {
          id
          title
          fdaProduct {
            id
            name
            elevenDigitNdc
            deaSchedule
          }
          upcProduct {
            id
            upc
          }
        }
      }
      client {
        id
        companyName
      }
      # Pick → Verify → Ship (Option 2): the server's resolution for THIS order (its stamp wins over
      # the company setting), read in this network-only document because it decides where the
      # Start button goes. The workflow is 'standard' for every Option-1 order, which leaves the
      # Summary exactly as it was (see @/lib/pickVerifyShip).
      fulfillmentWorkflow {
        workflow
        stage
        started
        pickerUserId
        pickerName
        pickerClaimStale
      }
    }
    # Bundled shipping context — a SIBLING top-level field in the same document so a handheld pays
    # no second round trip. Served from the primary, so membership is never a replica-lag stale read.
    shippingBundleContext(companyId: $vendorId, salesOrderId: $id) {
      eligibility {
        bundleable
        reason
      }
      bundle {
        id
        bundleNumber
        status
        clientId
        orders {
          ...BundleCandidateFields
        }
      }
      joinableBundle {
        id
        bundleNumber
        status
        clientId
        orders {
          ...BundleCandidateFields
        }
      }
      otherOrdersInFulfillment {
        ...BundleCandidateFields
      }
    }
  }
`;

export const BUNDLE_SET_ASIDE = gql`
  mutation shippingBundleSetAside($companyId: ID!, $salesOrderId: ID!) {
    shippingBundleSetAside(companyId: $companyId, salesOrderId: $salesOrderId) {
      id
      bundleNumber
    }
  }
`;

export const BUNDLE_ADD_ORDER = gql`
  mutation shippingBundleAddOrder($companyId: ID!, $shippingBundleId: ID!, $salesOrderId: ID!) {
    shippingBundleAddOrder(
      companyId: $companyId
      shippingBundleId: $shippingBundleId
      salesOrderId: $salesOrderId
    ) {
      id
      bundleNumber
    }
  }
`;

export const BUNDLE_REMOVE_ORDER = gql`
  mutation shippingBundleRemoveOrder($companyId: ID!, $salesOrderId: ID!) {
    shippingBundleRemoveOrder(companyId: $companyId, salesOrderId: $salesOrderId) {
      id
    }
  }
`;

/**
 * The box documents printed at Verify, re-printed from here for a verified order waiting to ship.
 * Its own query, asked only for such an order: kept out of CLIENT_ORDER so a failed document read
 * can never take the Summary down with it (errorPolicy 'all' — a missing document just hides the
 * one-tap reprint), and so every other order's Summary pays nothing for it.
 */
export const SUMMARY_PACK_DOCUMENTS = gql`
  query SalesOrderSummaryPackDocuments($id: ID!, $vendorId: ID!) {
    salesOrder(id: $id, vendorId: $vendorId) {
      id
      packInvoiceDocument(companyId: $vendorId) {
        id
        humanFilename
        link
      }
      packPackingListDocument(companyId: $vendorId) {
        id
        humanFilename
        link
      }
      packStickersDocument(companyId: $vendorId) {
        id
        humanFilename
        link
      }
    }
  }
`;

// The reverse of the standard picking screen's Complete: vendorStatus shipping → picking.
const RETURN_TO_PICKING = gql`
  mutation salesOrderUpdate(
    $orderId: ID!
    $orderAttributes: OrderAttributes!
    $companyId: ID!
  ) {
    salesOrderUpdate(
      orderId: $orderId
      orderAttributes: $orderAttributes
      companyId: $companyId
    ) {
      id
      vendorStatus
    }
  }
`;

// Pick → Verify → Ship: a verified order waiting to ship goes back to picking at the Verify
// step, and the pack copies printed at Verify are archived. A raw status write would leave
// those documents live.
const REOPEN_VERIFY = gql`
  mutation SalesOrderVerifyReopen($companyId: ID!, $salesOrderId: ID!) {
    salesOrderVerifyReopen(companyId: $companyId, salesOrderId: $salesOrderId) {
      salesOrderId
      vendorStatus
    }
  }
`;

function SalesOrderSummaryData(props) {
  const { id } = useParams();
  const vendorId = useRootStore((state) => state.selectedCompany.id);
  const { data, error, loading, refetch } = useQuery(CLIENT_ORDER, {
    variables: { id, vendorId },
    fetchPolicy: 'network-only'
  });
  const [bundleSetAside] = useMutation(BUNDLE_SET_ASIDE);
  const [bundleAddOrder] = useMutation(BUNDLE_ADD_ORDER);
  const [bundleRemoveOrder] = useMutation(BUNDLE_REMOVE_ORDER);
  const [returnToPicking] = useMutation(RETURN_TO_PICKING);
  const [reopenVerify] = useMutation(REOPEN_VERIFY);
  const { send: sendToPrinter, sendingKey } = useSendDocumentsToPrinter(vendorId as string);
  const rxPrintEnabled = useRxPrintEnabled(vendorId as string);
  const { data: packData } = useQuery(SUMMARY_PACK_DOCUMENTS, {
    variables: { id, vendorId },
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
    skip: pickVerifyStepFor(data?.salesOrder) !== 'documents'
  });

  if (loading) return <Spinner />;
  if (error) return <ApiError error={error} />;

  const { salesOrder, shippingBundleContext } = data || {};

  return (
    <SalesOrderSummary
      salesOrder={salesOrder}
      bundleContext={shippingBundleContext}
      bundleSetAside={bundleSetAside}
      bundleAddOrder={bundleAddOrder}
      bundleRemoveOrder={bundleRemoveOrder}
      returnToPicking={returnToPicking}
      reopenVerify={reopenVerify}
      rxPrintEnabled={rxPrintEnabled}
      packDocuments={packData?.salesOrder ?? null}
      sendingKey={sendingKey}
      onSendToPrinter={(key, documents) => sendToPrinter(key, salesOrder?.id, documents)}
      refetch={refetch}
      {...props}
    />
  );
}

export default SalesOrderSummaryData;
